import React, { useEffect } from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  updateCallStatus,
  syncAndFetchMissedCalls,
} from "../../../../redux/modules/missedCalls";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";

const styles = (theme) => ({
  dialogPaper: {
    minHeight: "60vh",
    maxHeight: "60vh",
    minWidth: "50vw",
    backgroundColor: "#424242",
  },
  table: {
    minWidth: 450,
  },
  tableCell: {
    color: "white",
    borderBottom: "none",
    padding: "8px 16px",
  },
  headerCell: {
    color: "white",
    borderBottom: "none",
    padding: "8px 16px",
  },
  actionsHeader: {
    color: "white",
    borderBottom: "none",
    padding: "8px 16px",
    textAlign: "right",
    paddingRight: "16px",
  },
  actionsCell: {
    color: "white",
    borderBottom: "none",
    padding: "8px 16px",
    textAlign: "right",
  },
  actionsContainer: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  tableHead: {
    backgroundColor: "#2196f3",
    "& th": {
      fontWeight: "bold",
      fontSize: "14px",
      color: "white",
    },
  },
  select: {
    color: "white",
    minWidth: 130,
    "&:before": {
      borderColor: "transparent",
    },
    "&:after": {
      borderColor: "transparent",
    },
    "& .MuiSelect-icon": {
      color: "white",
    },
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "#2196f3",
    },
    "&.Mui-selected": {
      backgroundColor: "#2196f3",
      "&:hover": {
        backgroundColor: "#1976d2",
      },
    },
  },
  actionButton: {
    color: "white",
    padding: 8,
    marginLeft: 4,
  },
  modalTitle: {
    borderBottom: "2px solid #2196f3",
    marginBottom: 16,
    paddingBottom: 8,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
});

const statusOptions = ["Cevapsız", "İşlemde", "Tamamlandı"];

const getStatusColor = (status) => {
  switch (status) {
    case "Cevapsız":
      return "#ff0000";
    case "İşlemde":
      return "#2196f3";
    case "Tamamlandı":
      return "#4caf50";
    default:
      return "#9e9e9e";
  }
};

const StatusCircle = ({ status }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <div
      style={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        backgroundColor: getStatusColor(status),
        marginRight: 8,
      }}
    />
    {status}
  </div>
);

const MissedCallsModal = ({
  classes,
  open,
  onClose,
  missedCalls,
  updateCallStatus,
  syncAndFetchMissedCalls,
  loading,
  error,
}) => {
  useEffect(
    () => {
      if (open) {
        syncAndFetchMissedCalls();
      }
    },
    [open, syncAndFetchMissedCalls]
  );

  const handleStatusChange = (event, callId) => {
    updateCallStatus(callId, event.target.value);
  };

  if (!missedCalls && !loading) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className={classes.modalTitle}>
        <Typography variant="h6" style={{ color: "white" }}>
          Cevapsız Çağrılar
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            style={{ backgroundColor: "#424242" }}
          >
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headerCell}>Durum</TableCell>
                  <TableCell className={classes.headerCell}>Tarih</TableCell>
                  <TableCell className={classes.headerCell}>Numara</TableCell>
                  <TableCell className={classes.headerCell}>
                    İşlem Yapan
                  </TableCell>
                  <TableCell className={classes.actionsHeader}>
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(missedCalls) &&
                  missedCalls.map((call) => (
                    <TableRow key={call.uniqueid} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        <StatusCircle status={call.status} />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {call.date}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {call.phoneNumber}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {call.operator}
                      </TableCell>
                      <TableCell className={classes.actionsCell}>
                        <div className={classes.actionsContainer}>
                          <Select
                            value={call.status}
                            onChange={(e) =>
                              handleStatusChange(e, call.uniqueid)
                            }
                            className={classes.select}
                            style={{ color: getStatusColor(call.status) }}
                          >
                            {statusOptions.map((status) => (
                              <MenuItem
                                key={status}
                                value={status}
                                className={classes.menuItem}
                              >
                                <StatusCircle status={status} />
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const missedCallsState = state.get("missedCallsReducer");
  return {
    missedCalls: missedCallsState
      ? missedCallsState.get("missedCalls").toJS()
      : [],
    loading: missedCallsState ? missedCallsState.get("loading") : false,
    error: missedCallsState ? missedCallsState.get("error") : null,
  };
};

const mapDispatchToProps = {
  updateCallStatus,
  syncAndFetchMissedCalls,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(MissedCallsModal));
